import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Search, Megaphone, Wrench, X, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getStatusColor,
  getCategoryLabel,
  type WaTemplate,
  type WaTemplateCategory,
} from "@/data/whatsappTemplateMocks";
import { useWaTemplates } from "@/hooks/useWaTemplates";

interface WhatsAppTemplatePickerProps {
  onClose: () => void;
  onSend: (template: WaTemplate) => void;
}

export const WhatsAppTemplatePicker = ({ onClose, onSend }: WhatsAppTemplatePickerProps) => {
  const { t } = useTranslation("chat");
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<'ALL' | WaTemplateCategory>('ALL');

  const { templates } = useWaTemplates();
  const approvedTemplates = templates.filter(t => t.status === 'APPROVED');

  const filtered = approvedTemplates.filter(t => {
    const matchSearch = t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.body.toLowerCase().includes(search.toLowerCase());
    const matchCategory = filterCategory === 'ALL' || t.category === filterCategory;
    return matchSearch && matchCategory;
  });

  return (
    <div className="w-[380px] max-h-[480px] bg-white border border-[#E5E7EB] rounded-xl shadow-sm flex flex-col overflow-hidden transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] dark:border-[#3F3F46]">
        <h3 className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("pickers.waTemplates")}</h3>
        <button
          onClick={onClose}
          className="text-[#64748B] hover:text-[#2563EB] transition-colors duration-200 dark:text-[#A1A1AA] dark:hover:text-[#60A5FA]"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-3 py-2 border-b border-[#E5E7EB] space-y-2 dark:border-[#3F3F46]">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8] dark:text-[#71717A]" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t("common.searchTemplate")}
            className="h-8 pl-8 text-sm rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
          />
        </div>
        <div className="flex gap-1">
          {(['ALL', 'UTILITY', 'MARKETING'] as const).map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors duration-200 ${
                filterCategory === cat
                  ? 'bg-[#EEF4FF] text-[#2563EB] dark:bg-[rgba(37,99,235,.18)] dark:text-[#60A5FA]'
                  : 'text-[#64748B] hover:text-[#2563EB] hover:bg-[#F8FAFC] dark:text-[#A1A1AA] dark:hover:text-[#60A5FA] dark:hover:bg-[#3F3F46]'
              }`}
            >
              {cat === 'ALL' ? t('pickers.allCategories') : getCategoryLabel(cat)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <p className="text-sm text-[#94A3B8] dark:text-[#71717A]">{t("pickers.noApproved")}</p>
          </div>
        ) : (
          <div className="py-1">
            {filtered.map(template => {
              const status = getStatusColor(template.status);
              return (
                <button
                  key={template.id}
                  onClick={() => onSend(template)}
                  className="w-full text-left px-4 py-3 hover:bg-[#F8FAFC] transition-colors duration-200 flex items-center gap-3 border-b border-[#E5E7EB]/60 last:border-b-0 dark:hover:bg-[#3F3F46]/50 dark:border-[#3F3F46]/50"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    template.category === 'MARKETING'
                      ? 'bg-[#EEF4FF] dark:bg-blue-500/15'
                      : 'bg-[#ECFDF5] dark:bg-emerald-500/15'
                  }`}>
                    {template.category === 'MARKETING'
                      ? <Megaphone className="w-3.5 h-3.5 text-[#2563EB] dark:text-[#60A5FA]" />
                      : <Wrench className="w-3.5 h-3.5 text-[#16A34A] dark:text-emerald-400" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-[13px] font-medium text-[#0F172A] font-mono truncate dark:text-[#FAFAFA]">{template.name}</p>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${status.bg} ${status.text} flex-shrink-0`}>
                        {status.label}
                      </span>
                    </div>
                    <p className="text-xs text-[#64748B] line-clamp-1 dark:text-[#A1A1AA]">{template.body}</p>
                  </div>
                  <Send className="w-4 h-4 text-[#94A3B8] flex-shrink-0 dark:text-[#71717A]" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
